/**
 * Vorlage „Abkühlen lassen“.
 *
 * Das Gebäck liegt auf einem Kuchengitter, damit es auch von unten auskühlt.
 * Der Dampf wird mit der Zeit immer weniger.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { compact, ellipse, line, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { steam } from '../parts/effects.ts';
import { cake } from '../parts/food.ts';
import { coolingRack } from '../parts/kitchen.ts';
import { arrow, BADGE_RIGHT, counter, durationLabel, GROUND_Y, timerBadge } from '../parts/stage.ts';
import { minutesFromText } from '../inference.ts';
import { choice, defineTemplate, numberValue, textValue } from '../template.ts';

const RACK = { cx: 112, top: 120, width: 160 } as const;

/** Zeitplan. */
const TIME = { cool: [0.04, 0.86], warm: [0.9, 1] } as const;

/** Gebäckarten. */
const ITEMS = { cake: 'Kuchen', bread: 'Brot', cookies: 'Plätzchen' } as const;
type Item = keyof typeof ITEMS;

/** Gebäck auf dem Gitter; die Unterkante liegt auf RACK.top. */
function bake(item: Item): SceneNode[] {
  if (item === 'bread') {
    const loaf = path()
      .move(RACK.cx - 50, RACK.top)
      .curve(RACK.cx - 56, RACK.top - 46, RACK.cx + 56, RACK.top - 46, RACK.cx + 50, RACK.top)
      .close()
      .toString();
    const cuts: SceneNode[] = [-24, 0, 24].map((dx) =>
      shape(
        path()
          .move(RACK.cx + dx - 8, RACK.top - 26)
          .quad(RACK.cx + dx, RACK.top - 34, RACK.cx + dx + 8, RACK.top - 28)
          .toString(),
        {
          stroke: 'deep',
          strokeWidth: STROKE.outline,
        },
      ),
    );
    return [shape(loaf, styles.solid('shade')), ...cuts];
  }
  if (item === 'cookies') {
    return [-54, -18, 18, 54].flatMap((dx) => [
      ellipse(RACK.cx + dx, RACK.top - 4, 15, 5, styles.solid('shade')),
      line(RACK.cx + dx - 15, RACK.top - 4, RACK.cx + dx - 15, RACK.top - 1, styles.detail()),
      ellipse(RACK.cx + dx, RACK.top - 6, 15, 5, styles.solid('tint')),
    ]);
  }
  const width = 112;
  const ry = width * 0.16;
  return cake(RACK.cx, RACK.top - ry + 1, width, 26, 4);
}

/** Vorlage „Abkühlen lassen“. */
export const bakeCoolTemplate = defineTemplate({
  id: 'bake.cool',
  group: 'bake',
  title: 'Abkühlen lassen',
  description:
    'Das Gebäck kühlt auf einem Kuchengitter aus, damit von unten Luft herankommt und es nicht feucht wird.',
  durationSeconds: 5,
  keywords: ['abkühlen', 'auskühlen', 'erkalten', 'kuchengitter', 'gitter', 'lauwarm', 'kalt werden'],
  stillTime: 0.2,
  parameters: [
    choice('item', 'Gebäck', ITEMS, 'cake'),
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 5,
      max: 240,
      step: 5,
      unit: 'Min',
      defaultValue: 30,
    },
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      minutes: minutesFromText(text),
      item: /brot|brötchen|baguette/.test(lower)
        ? 'bread'
        : /plätzchen|kekse|cookies/.test(lower)
          ? 'cookies'
          : /kuchen|torte|boden/.test(lower)
            ? 'cake'
            : undefined,
    };
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'cake') as Item;
    const minutes = numberValue(values, 'minutes', 30);
    const heat =
      lerp(1, 0.12, ease.inOutSine(segment(t, ...TIME.cool))) +
      0.88 * ease.inOutSine(segment(t, ...TIME.warm));
    const steamTop = item === 'bread' ? RACK.top - 40 : item === 'cookies' ? RACK.top - 14 : RACK.top - 40;
    return compact([
      ...counter(),
      ...coolingRack(RACK.cx, RACK.top, RACK.width, GROUND_Y),
      ...bake(item),
      ...steam(RACK.cx, steamTop, t, { count: 3, spread: 30, height: 34, strength: heat }),
      timerBadge(BADGE_RIGHT, 24, durationLabel(minutes), t),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: RACK.cx - 70, y: GROUND_Y - 4 },
        { x: RACK.cx - 40, y: GROUND_Y - 10 },
        { x: RACK.cx - 10, y: GROUND_Y - 12 },
      ]),
    ];
  },
});
